import {
  loadGoogleMaps as load,
  tilesAreConfiguredWith,
  type GoogleMapsGlobal,
} from "@tdelivr/google-maps";

/**
 * This application's Google Maps key, and nothing else.
 *
 * The loader itself is `@tdelivr/google-maps`, shared with the dispatch console. What stays here
 * is the environment read, because Vite bakes `import.meta.env.VITE_*` in at build time per
 * application: a package reaching for the variable would only work in the application that named
 * it. Mirrors `apps/dispatch-console/src/google-maps.ts` for the same reason.
 *
 * ## Without a key this application draws exactly what it drew before
 *
 * `MapHero`'s schematic — the grid, two roads, the park and the pins at the design's own
 * percentages — is not a fallback. It is what `10 Rider App.dc.html` draws, and its approved
 * reference screenshots show it at all three widths. Every test runs with no key and no network,
 * and a rider who cannot see the screen because a billing account lapsed is worse off than one
 * looking at a schematic. So tiles are an enhancement over a surface that already works.
 */
export const MAPS_API_KEY: string | undefined =
  typeof import.meta.env.VITE_GOOGLE_MAPS_API_KEY === "string" &&
  import.meta.env.VITE_GOOGLE_MAPS_API_KEY.length > 0
    ? import.meta.env.VITE_GOOGLE_MAPS_API_KEY
    : undefined;

/** Whether this build should draw tiles at all. One question, asked in one place. */
export function tilesAreConfigured(): boolean {
  return tilesAreConfiguredWith(MAPS_API_KEY);
}

/** The API, loaded once for this application, under this application's key. */
export function loadGoogleMaps(): Promise<GoogleMapsGlobal> {
  return load(MAPS_API_KEY);
}

export {
  boundsOf,
  MAP_OPTIONS,
  onMapsAuthFailure,
  projectionFromMap,
  type Coordinate,
  type GoogleMap,
  type GoogleMapsGlobal,
  type Placement,
  type Projection,
} from "@tdelivr/google-maps";
